/**
 * `<TopDownView>` — a TopDown screen: Sector → Industry → Symbols in one panel
 * (`docs/features/topdown.md`, mock `docs/topdown-screener-mock.html`).
 *
 * The Symbols grid is an ordinary `ScreenerView` over the **base** screen, read
 * live from that screen rather than copied, with the ticks ANDed into its
 * filter on the way to the grid. The Sector and Industry grids are plain
 * tables: their rows come from `POST /sym/topdown/levels`, where every column
 * is evaluated on the group's index bars — never a sum or mean of members.
 *
 * What this owns, and nothing else does: the ticks (saved with the panel), the
 * layout (auto follows the dock), and what a tick charts — the index of the
 * ticked groups, through `lib/topdown.registerIndex`.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import type { ScreenerColumn, ScreenerConfig, ScreenerTopDown } from "@qufolio/model";
import type { PresetDefinition, ScreenerConfigPatch } from "@qufolio/screener-grid";

import { fetchPreset, saveScreen } from "../api/presets";
import { fetchLevels, topDownConfigured } from "../api/topdown";
import { isOwn, useScreens } from "../lib/screens";
import {
  indexLabel,
  indexMembers,
  layoutFor,
  levelsAsk,
  levelsKey,
  packTicks,
  registerIndex,
  setAllTicks,
  ticksOf,
  toggleTick,
  upgradedLevels,
  visibleIndustries,
  withMemberValues,
  type LevelKey,
  type LevelRow,
  type MemberRow,
} from "../lib/topdown";
import { ScreenerView } from "./ScreenerView";

export interface TopDownViewProps {
  config: ScreenerConfig & { topDown: ScreenerTopDown };
  /** Which side of the charts the screener is docked on — `auto`'s input. */
  side?: "left" | "right" | "top" | "bottom";
  onSelectSymbol?: (symbol: string) => void;
  onConfigChange?: (patch: ScreenerConfigPatch) => void;
  onHide?: () => void;
  /** The bar's ＋ New and ✎ Edit (`ScreenerBar`), as on any screener panel. */
  onNewScreen?: () => void;
  onEditScreen?: () => void;
}

/** How often the level rows are asked for again with nothing changed — today's bar moves. */
const REFRESH_MS = 60_000;
/** How long the members must hold still before the rows are asked for. */
const SETTLE_MS = 400;

export function TopDownView({
  config, side, onSelectSymbol, onConfigChange, onHide, onNewScreen, onEditScreen,
}: TopDownViewProps) {
  const td = config.topDown;
  // A TopDown made before fM existed picks up today's columns once.
  useEffect(() => {
    const next = upgradedLevels(td);
    if (next) onConfigChange?.({ topDown: next });
  }, [td, onConfigChange]);
  const { screens } = useScreens();
  const self = screens.find((s) => s.key === config.screenRef);
  const baseSummary = screens.find((s) => s.key === td.base);

  // ── the base screen, live ────────────────────────────────────────────────
  const [base, setBase] = useState<PresetDefinition | null | "missing">(null);
  const baseVersion = baseSummary?.version;
  useEffect(() => {
    let live = true;
    setBase(null);
    void fetchPreset(td.base).then((found) => {
      if (live) setBase(found ?? "missing");
    });
    return () => {
      live = false;
    };
  }, [td.base, baseVersion]);

  /**
   * The Symbols grid's config: this panel, showing the base screen's columns,
   * filter and sort. **No `screenRef`**, so the inner view neither refills from
   * a screen nor writes one back — the base is edited in its own editor.
   */
  // Built from the fields it reads, not from `config`: a tick patches `config`,
  // and a new object here on every tick would rebuild the grid under the user.
  //
  // **The conditions are this panel's**, ticked in the Conditions popup over
  // the base screen's library, and they are what the funnel lights for.
  // `activeFilterRefs` was `[]` here, so a ticked A+ showed in the popup and did
  // nothing. **The base screen's stored `filter` is never applied** — it is a
  // saved copy of whatever was ticked when that screen was last saved, in no
  // popup, and it cut SP500 501 to 116 with nothing ticked (owner, 2026-10-18).
  const { id, group, screen, extendedHours, savedFilters, filter, activeFilterRefs, activeFilterRef } = config;
  const baseConfig = useMemo<ScreenerConfig | null>(() => {
    if (!base || base === "missing") return null;
    return {
      id,
      group,
      screen,
      ...(extendedHours !== undefined ? { extendedHours } : {}),
      columns: base.columns ?? [],
      ...(filter ? { filter } : {}),
      sort: base.sort ?? [],
      savedFilters: base.savedFilters ?? savedFilters ?? [],
      savedFilterCategoryOrder: base.savedFilterCategoryOrder ?? [],
      activeFilterRefs: activeFilterRefs ?? [],
      ...(activeFilterRef ? { activeFilterRef } : {}),
    };
  }, [base, id, group, screen, extendedHours, savedFilters, filter, activeFilterRefs, activeFilterRef]);

  // ── members, ticks ───────────────────────────────────────────────────────
  const [members, setMembers] = useState<MemberRow[]>([]);
  const membersSig = useRef("");
  const onMembers = useCallback((rows: MemberRow[]) => {
    const sig = rows.map((r) => `${r.symbol}|${r.sector ?? ""}|${r.industry ?? ""}`).join(",");
    if (sig === membersSig.current) return;
    membersSig.current = sig;
    setMembers(rows);
  }, []);

  const ticks = useMemo(() => ticksOf(td), [td]);
  const writeTicks = useCallback(
    (next: { sector: string[]; industry: string[] }) => {
      const packed = packTicks(next.sector, next.industry);
      const { ticks: _old, ...rest } = td;
      onConfigChange?.({ topDown: packed ? { ...rest, ticks: packed } : rest });
      // Ticked groups chart as one index; industries win over sectors; nothing
      // ticked leaves the chart where it is (owner, 2026-10-16).
      const picked = indexMembers(members, next);
      if (picked && onSelectSymbol) onSelectSymbol(registerIndex(picked, indexLabel(next), td.weighting));
    },
    [td, onConfigChange, members, onSelectSymbol],
  );
  const topDownProp = useMemo(
    () => ({ ticks, onMembers, conditionsScreen: td.base }),
    [ticks, onMembers, td.base],
  );

  // ── level rows ───────────────────────────────────────────────────────────
  const [levels, setLevels] = useState<Partial<Record<LevelKey, LevelRow[]>>>({});
  const [levelError, setLevelError] = useState<string | null>(null);
  const [pulse, setPulse] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setPulse((n) => n + 1), REFRESH_MS);
    return () => window.clearInterval(id);
  }, []);
  const askKey = useMemo(() => levelsKey(members, td), [members, td]);
  useEffect(() => {
    if (!topDownConfigured || members.length === 0) return;
    const abort = new AbortController();
    const timer = window.setTimeout(() => {
      void fetchLevels(members, levelsAsk(td), abort.signal, td.weighting ?? "equal").then((result) => {
        if (abort.signal.aborted) return;
        if (result.ok) {
          setLevels(result.answer.levels);
          setLevelError(null);
        } else {
          setLevelError(result.reason);
        }
      });
    }, SETTLE_MS);
    return () => {
      window.clearTimeout(timer);
      abort.abort();
    };
    // `askKey` stands for members + columns; `pulse` is the minute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askKey, pulse]);

  // ── the TopDown's own definition, back to its screen ─────────────────────
  // The panel's ordinary sync runs inside `ScreenerView`, which a TopDown
  // section does not render for itself, so layout and level columns are
  // written here: debounced, own screens only, never the ticks.
  const definition = useMemo(() => {
    const { ticks: _t, ...rest } = td;
    return JSON.stringify(rest);
  }, [td]);
  const savedDefinition = useRef(definition);
  useEffect(() => {
    if (!self || !isOwn(self) || definition === savedDefinition.current) return;
    const timer = window.setTimeout(() => {
      savedDefinition.current = definition;
      void saveScreen(self.key, {
        name: self.name,
        market: self.market,
        groupCodes: self.groupCodes,
        topDown: JSON.parse(definition) as ScreenerTopDown,
      }).catch(() => {
        savedDefinition.current = "";
      });
    }, 600);
    return () => window.clearTimeout(timer);
  }, [definition, self]);

  // ── layout ───────────────────────────────────────────────────────────────
  const rootRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry?.contentRect.width ?? 0));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const shape = layoutFor(td.orientation, side, width);

  const industryFilter = useMemo(() => visibleIndustries(ticks.sector, members), [ticks.sector, members]);
  const columnsOf = (key: LevelKey) => td.levels.find((l) => l.key === key)?.columns?.filter((c) => !c.hide) ?? [];
  const baseName = baseSummary?.name ?? (base && base !== "missing" ? base.name : td.base);

  const grid = (key: LevelKey, title: string) => (
    <LevelGrid
      key={key}
      title={title}
      rows={withMemberValues(levels[key] ?? [], members, key).filter(
        (r) => key !== "industry" || !industryFilter || industryFilter.has(r.key),
      )}
      columns={columnsOf(key)}
      ticked={ticks[key]}
      onToggle={(name) => writeTicks(toggleTick(ticks, key, name, members))}
      onAll={(names, on) => writeTicks(setAllTicks(ticks, key, names, on, members))}
    />
  );

  /**
   * The screener bar sits on top (owner, 2026-10-16), so this panel has no
   * header of its own: the layout is the bar's settings slot, the ticks show
   * in the grids' own boxes (the header box unticks them all), the count is
   * the bar's ⧩, and Hide is its ⋯. The levels are laid out around the
   * Symbols grid *under* the bar, through `frameGrid`.
   */
  // The layout lives in the bar's ⋯ (owner, 2026-10-16), not on the bar.
  const layoutMenu = {
    heading: "TopDown layout",
    options: (["auto", "tall", "wide"] as const).map((o) => ({
      label: { auto: "Auto", tall: "Levels on top", wide: "One row" }[o],
      active: (td.orientation ?? "auto") === o,
      onSelect: () => onConfigChange?.({ topDown: { ...td, orientation: o } }),
    })),
  };

  const framed = (symbols: ReactNode) => (
    <div style={{ ...(shape === "wide" ? WIDE : TALL), height: "100%" }}>
      <div style={shape === "wide" ? { display: "contents" } : TOP}>
        {grid("sector", "Sector")}
        {grid("industry", "Industry")}
      </div>
      {/* The grid fills its cell: `body()` has no height of its own. */}
      <div style={SYMBOLS}>
        <div style={{ flex: "1 1 auto", minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }}>
          {symbols}
        </div>
      </div>
    </div>
  );

  return (
    <div ref={rootRef} style={ROOT}>
      {levelError && <div style={NOTICE}>Sector and industry rows: {levelError}</div>}
      {base === "missing" ? (
        framed(<div style={NOTICE}>The Symbols screen ({td.base}) is not one you can open.</div>)
      ) : baseConfig ? (
        <ScreenerView
          config={baseConfig}
          onSelectSymbol={onSelectSymbol}
          topDown={topDownProp}
          screenHost={{ config, onConfigChange }}
          moreSection={layoutMenu}
          frameGrid={framed}
          onHide={onHide}
          onNewScreen={onNewScreen}
          onEditScreen={onEditScreen}
          dockSide={side}
        />
      ) : (
        framed(<div style={NOTICE}>Loading {baseName}…</div>)
      )}
    </div>
  );
}

// ── one level grid ─────────────────────────────────────────────────────────

function LevelGrid({
  title,
  rows,
  columns,
  ticked,
  onToggle,
  onAll,
}: {
  title: string;
  rows: LevelRow[];
  columns: ScreenerColumn[];
  ticked: readonly string[];
  onToggle: (name: string) => void;
  onAll: (names: string[], on: boolean) => void;
}) {
  const [sort, setSort] = useState<{ field: string; dir: 1 | -1 }>({ field: "", dir: 1 });
  const sorted = useMemo(() => {
    const out = [...rows];
    out.sort((a, b) => {
      if (!sort.field) return a.key.localeCompare(b.key) * sort.dir;
      const x = a.values[sort.field];
      const y = b.values[sort.field];
      if (x == null) return 1;
      if (y == null) return -1;
      return (x - y) * sort.dir;
    });
    return out;
  }, [rows, sort]);
  const by = (field: string) =>
    setSort((s) => ({ field, dir: s.field === field ? ((-s.dir) as 1 | -1) : field ? -1 : 1 }));
  const names = sorted.map((r) => r.key);
  const all = names.length > 0 && names.every((n) => ticked.includes(n));
  return (
    <div style={GRID} role="region" aria-label={title}>
      <table style={TABLE}>
        <thead>
          <tr>
            <th style={{ ...TH, width: 26 }}>
              <input
                type="checkbox"
                aria-label={`Tick every ${title.toLowerCase()}`}
                checked={all}
                onChange={(e) => onAll(names, e.target.checked)}
              />
            </th>
            <th style={TH} onClick={() => by("")}>
              {title}
            </th>
            {columns.map((c) => (
              <th key={c.field} style={{ ...TH, textAlign: "right" }} onClick={() => by(c.field)}>
                {c.header ?? c.field}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const on = ticked.includes(row.key);
            return (
              <tr key={row.key} style={on ? ROW_ON : undefined} onClick={() => onToggle(row.key)}>
                <td style={TD}>
                  <input
                    type="checkbox"
                    aria-label={`Tick ${row.key}`}
                    checked={on}
                    onChange={() => onToggle(row.key)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td style={{ ...TD, ...NAME }} title={`${row.key} — ${row.members} in its index`}>
                  {row.key}
                </td>
                {columns.map((c) => {
                  const v = row.values[c.field];
                  return (
                    <td key={c.field} style={{ ...TD, textAlign: "right", color: tone(v) }}>
                      {v == null ? "" : v.toFixed(c.precision ?? 2)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function tone(v: number | null | undefined): string | undefined {
  if (v == null || v === 0) return undefined;
  return v > 0 ? "var(--qf-positive)" : "var(--qf-negative)";
}

const ROOT: CSSProperties = { display: "flex", flexDirection: "column", width: "100%", height: "100%", minHeight: 0, background: "var(--qf-background)" };
const NOTICE: CSSProperties = { padding: 8, fontSize: 12, color: "var(--qf-text-muted)" };
const TALL: CSSProperties = { flex: "1 1 auto", minHeight: 0, display: "grid", gridTemplateRows: "minmax(160px, 40%) 1fr", gap: 3, background: "var(--qf-surface)" };
const TOP: CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, minHeight: 0 };
const WIDE: CSSProperties = {
  flex: "1 1 auto", minHeight: 0, display: "grid", gap: 3, background: "var(--qf-surface)",
  gridTemplateColumns: "minmax(280px, 0.9fr) minmax(300px, 1fr) minmax(520px, 2fr)",
};
const SYMBOLS: CSSProperties = { minHeight: 0, minWidth: 0, display: "flex", flexDirection: "column", background: "var(--qf-background)" };
const GRID: CSSProperties = { overflow: "auto", minHeight: 0, minWidth: 0, background: "var(--qf-background)" };
const TABLE: CSSProperties = { borderCollapse: "collapse", width: "100%", fontSize: 12, fontVariantNumeric: "tabular-nums", color: "var(--qf-text)" };
const TH: CSSProperties = {
  position: "sticky", top: 0, textAlign: "left", padding: "3px 8px", cursor: "pointer", whiteSpace: "nowrap",
  background: "var(--qf-surface)", borderBottom: "1px solid var(--qf-border)", fontWeight: 600,
};
const TD: CSSProperties = { padding: "3px 8px", borderBottom: "1px solid var(--qf-border)", whiteSpace: "nowrap", cursor: "pointer" };
const NAME: CSSProperties = { maxWidth: 210, overflow: "hidden", textOverflow: "ellipsis" };
const ROW_ON: CSSProperties = { background: "var(--qf-selection, rgba(88,166,255,0.15))" };
